import { createLayoutPackageAPI } from '@/api/layout';
import { Box, TextField } from '@mui/material';
import localforage from 'localforage';
import BaseDialog from '../ui/BaseDialog';
import { useLayoutState } from '@/provider/layouts/layoutContext';
import { useCallback } from 'react';
import { useAppContext } from '@/provider/contexts';
import { useNavigate } from 'react-router-dom';
import { CreateLayoutPackageRequest } from '@/types/pages/layout';
import { useLayoutUtils } from '@/hooks/layouts/useLayoutUtils';
import { hideLoading, showLoading } from '../ui/LoadingOverlay';
import { useTranslation } from 'react-i18next';

const SaveAsDialog = () => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const { setNotification } = useAppContext();
    const { isNew, getSinglePayload, getMultiPayload } = useLayoutUtils();
    const { state, dispatch } = useLayoutState();

    const buildPayload = (): CreateLayoutPackageRequest => {
        return {
            package_name: state.packageName,
            package_description: '',
            create_empty_layout_package: false,
            packages: {
                ...getSinglePayload(),
                ...getMultiPayload(),
            },
        };
    };

    const handleOnConfirm = async () => {
        const saveAsName = state.saveAsName.trim();
        if (!saveAsName) {
            setNotification({ message: t('Please enter a package name.'), severity: 'warning' });
            return;
        }
        if (saveAsName === state.packageName.trim() && !isNew) {
            setNotification({
                message: t(
                    "Layout Package '{{name}}' already exists. Please use a different name or update the package.",
                    { name: state.packageName },
                ),
                severity: 'warning',
            });
            return;
        }
        showLoading();
        dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'saveAs', loading: true } });
        dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'saveAs', open: false } });
        try {
            const corpId = await localforage.getItem<number>('corporation_id');
            if (!corpId) throw new Error('No corpId');
            const payload = buildPayload();
            payload.package_name = saveAsName;
            const { data } = await createLayoutPackageAPI(corpId, payload);
            if (data.status === 400) {
                setNotification({ message: data.message, severity: 'warning' });
                return;
            }
            if (data.status !== 200) {
                setNotification({ message: t('Failed to save layout package.'), severity: 'error' });
                return;
            }
            const createdId = data.data.information.layout_package.id;
            dispatch({ type: 'SET_PACKAGE_NAME', payload: payload.package_name });
            setNotification({ message: t('Layout package created!'), severity: 'success' });
            navigate(`/admin/layouts/${createdId}`, {
                replace: true,
                state: { needAutoGeneratePreview: true },
            });
        } catch {
            setNotification({ message: t('Failed to save layout package.'), severity: 'error' });
        } finally {
            hideLoading();
            dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'saveAs', loading: false } });
        }
    };

    return (
        <BaseDialog
            open={state.dialogs.saveAs}
            dialogTitle={isNew ? t('New Layout Package') : t('Save As...')}
            confirmButtonText={t('Save')}
            cancelButtonText={t('Cancel')}
            disableConfirm={state.loadings.saveAs}
            onClose={useCallback(
                () => dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'saveAs', open: false } }),
                [],
            )}
            onConfirm={handleOnConfirm}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    width: '100%',
                }}
            >
                <Box sx={{ mt: 1 }}>
                    <TextField
                        fullWidth
                        autoFocus
                        disabled={isNew}
                        variant="outlined"
                        label={t('New layout package name')}
                        value={state.saveAsName}
                        onChange={(_event) => dispatch({ type: 'SET_SAVE_AS_NAME', payload: _event.target.value })}
                    />
                </Box>
            </div>
        </BaseDialog>
    );
};

export default SaveAsDialog;
